import { Check, ChevronDown, Palette } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { cn } from "../lib/utils";
import { BrandKit } from "../models/BrandKit";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

interface BrandKitSelectorProps {
  brandKits: BrandKit[];
  selectedKitId: string | null;
  onChange: (kitId: string | null) => void;
  /** Off where leaving the page would interrupt a flow, such as onboarding. */
  showManage?: boolean;
  /** The selected kit is applied but not in `brandKits` yet. */
  pending?: boolean;
}

export function BrandKitSelector({
  brandKits,
  selectedKitId,
  onChange,
  showManage = true,
  pending = false,
}: BrandKitSelectorProps) {
  const selectedKit = brandKits.find((kit) => kit.id === selectedKitId);
  const navigate = useNavigate();
  const isCustomized = !!selectedKit;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 gap-1.5 px-3 text-sm font-normal"
        >
          <Palette className="h-3.5 w-3.5 text-muted-foreground" />
          {selectedKit ? (
            <span className="font-medium text-foreground">
              {selectedKit.name}
            </span>
          ) : (
            <>
              <span className="text-muted-foreground">Brand Kit:</span>
              <span className="font-medium text-foreground">
                {pending ? "Loading…" : "None"}
              </span>
            </>
          )}
          <ChevronDown
            className={cn(
              "h-3.5 w-3.5",
              isCustomized ? "text-brand" : "text-muted-foreground"
            )}
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuItem onSelect={() => onChange(null)}>
          <div className="flex justify-between items-center w-full">
            <span>None</span>
            {!selectedKitId && (
              <>
                <Check
                  className="h-3.5 w-3.5 text-muted-foreground"
                  aria-hidden="true"
                />
                <span className="sr-only">Selected</span>
              </>
            )}
          </div>
        </DropdownMenuItem>
        {brandKits.map((kit) => (
          <DropdownMenuItem key={kit.id} onSelect={() => onChange(kit.id)}>
            <div className="flex justify-between items-center gap-2 w-full">
              <span className="truncate">{kit.name}</span>
              {kit.id === selectedKitId && (
                <>
                  <Check
                    className="h-3.5 w-3.5 text-muted-foreground shrink-0"
                    aria-hidden="true"
                  />
                  <span className="sr-only">Selected</span>
                </>
              )}
            </div>
          </DropdownMenuItem>
        ))}
        {showManage && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => navigate("/brand-kits")}>
              Manage Brand Kits
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
